/**
 * ============================================================================
 *  File        : frontend/src/pages/SecurityPage.tsx
 *  Module      : pages
 *  Purpose     : /admin/security 二要素認証（2FA / TOTP）設定（spec §A1）
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  - 未啟用:按「設定」→ setup（取得 otpauth URI + secret）→ 輸入 authenticator
 *    產生的 6 位 code → verify → 啟用。
 *  - 已啟用:輸入一組有效 code → disable → 停用。
 *  狀態以 GET /api/v1/me 的 two_factor_enabled 為準（操作後 invalidate 重取）。
 *  此頁本身不需要 step-up code（管理自己的 2FA 是 public-after-auth）。
 */

import { useState, type ReactElement } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { api, ApiError } from '../api/client'
import {
  disableTwoFactor,
  setupTwoFactor,
  verifyTwoFactor,
  type TotpSetupResponse,
} from '../api/twoFactor'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'

interface PageProps {
  t: (key: string) => string
}

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

/** 從 /me 讀出 two_factor_enabled（未知時 false）。 */
function readEnabled(me: unknown): boolean {
  if (typeof me !== 'object' || me === null) return false
  const v = (me as Record<string, unknown>)['two_factor_enabled']
  return v === true
}

export function SecurityPage({ t }: PageProps): ReactElement {
  const queryClient = useQueryClient()
  const meQuery = useQuery({ queryKey: ['me', '2fa'], queryFn: () => api.me() })

  if (meQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  const enabled = readEnabled(meQuery.data)

  return (
    <div className="space-y-4" data-testid="security-page">
      <header className="page-heading">
        <span className="eyebrow">{t('nav.admin')}</span>
        <h2 className="text-lg font-semibold">{t('twofa.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('twofa.subtitle')}</p>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck aria-hidden size={18} />
            {t('twofa.status')}
          </CardTitle>
          <Badge
            variant={enabled ? 'secondary' : 'outline'}
            data-testid="twofa-status"
            data-enabled={enabled ? 'true' : 'false'}
          >
            {enabled ? t('twofa.enabled') : t('twofa.disabled')}
          </Badge>
        </CardHeader>
        <CardContent>
          {enabled ? (
            <DisablePanel
              t={t}
              onDone={() => void queryClient.invalidateQueries({ queryKey: ['me', '2fa'] })}
            />
          ) : (
            <SetupPanel
              t={t}
              onEnabled={() => void queryClient.invalidateQueries({ queryKey: ['me', '2fa'] })}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ---- 設定（setup → verify）------------------------------------------------

function SetupPanel({
  t,
  onEnabled,
}: {
  t: (key: string) => string
  onEnabled: () => void
}): ReactElement {
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null)
  const [code, setCode] = useState('')

  const setupMutation = useMutation({
    mutationFn: () => setupTwoFactor(),
    onSuccess: (res) => {
      setSetup(res)
      toast.success(t('twofa.setup.ready'))
    },
    onError: (error) => toast.error(`${t('twofa.setup.error')}${errDetail(error)}`),
  })

  const verifyMutation = useMutation({
    mutationFn: (c: string) => verifyTwoFactor(c),
    onSuccess: () => {
      toast.success(t('twofa.verify.success'))
      setSetup(null)
      setCode('')
      onEnabled()
    },
    onError: (error) => toast.error(`${t('twofa.verify.error')}${errDetail(error)}`),
  })

  if (setup === null) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{t('twofa.setup.intro')}</p>
        <Button
          type="button"
          data-testid="twofa-setup-start"
          disabled={setupMutation.isPending}
          onClick={() => setupMutation.mutate()}
        >
          {setupMutation.isPending ? t('twofa.setup.starting') : t('twofa.setup.start')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">{t('twofa.setup.scan')}</p>
        <p className="text-xs text-muted-foreground">{t('twofa.setup.scanHint')}</p>
        {/* QR ライブラリ非依存:otpauth URI とシークレットを表示（手動入力対応）。 */}
        <code
          className="block break-all rounded-md border border-border bg-muted p-2 text-xs"
          data-testid="twofa-otpauth-uri"
        >
          {setup.otpauth_uri}
        </code>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('twofa.setup.secret')}</span>
          <code className="rounded bg-muted px-2 py-1 text-sm font-semibold" data-testid="twofa-secret">
            {setup.secret}
          </code>
        </div>
      </div>

      <form
        className="flex max-w-md flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          const c = code.trim()
          if (c) verifyMutation.mutate(c)
        }}
      >
        <label className="text-sm font-medium" htmlFor="twofa-verify-code">
          {t('twofa.verify.label')}
        </label>
        <Input
          id="twofa-verify-code"
          data-testid="twofa-verify-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          placeholder={t('twofa.codePlaceholder')}
          aria-label={t('twofa.verify.label')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button
          type="submit"
          data-testid="twofa-verify-submit"
          disabled={verifyMutation.isPending || code.trim() === ''}
        >
          {verifyMutation.isPending ? t('twofa.verify.submitting') : t('twofa.verify.submit')}
        </Button>
      </form>
    </div>
  )
}

// ---- 停用（disable）-------------------------------------------------------

function DisablePanel({
  t,
  onDone,
}: {
  t: (key: string) => string
  onDone: () => void
}): ReactElement {
  const [code, setCode] = useState('')

  const disableMutation = useMutation({
    mutationFn: (c: string) => disableTwoFactor(c),
    onSuccess: () => {
      toast.success(t('twofa.disable.success'))
      setCode('')
      onDone()
    },
    onError: (error) => toast.error(`${t('twofa.disable.error')}${errDetail(error)}`),
  })

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t('twofa.disable.intro')}</p>
      <form
        className="flex max-w-md flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          const c = code.trim()
          if (c) disableMutation.mutate(c)
        }}
      >
        <label className="text-sm font-medium" htmlFor="twofa-disable-code">
          {t('twofa.disable.label')}
        </label>
        <Input
          id="twofa-disable-code"
          data-testid="twofa-disable-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          placeholder={t('twofa.codePlaceholder')}
          aria-label={t('twofa.disable.label')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button
          type="submit"
          variant="destructive"
          data-testid="twofa-disable-submit"
          disabled={disableMutation.isPending || code.trim() === ''}
        >
          {disableMutation.isPending ? t('twofa.disable.submitting') : t('twofa.disable.submit')}
        </Button>
      </form>
    </div>
  )
}
